import type { FC, ReactNode } from 'react'
import type { CategoryCardData } from '@/types'

import Link from 'next/link'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useI18n } from '@/lib/i18n'
import { CmsText } from '@/components/store/cms-text'
import { Num } from '@/components/store/price'
import { categoryIcon } from '@/components/store/product-media'

interface CategoryCardProps {
    category: CategoryCardData
    className?: string
}

export const CategoryCard: FC<CategoryCardProps> = ({ category, className }): ReactNode => {
    const { tx, isRtl } = useI18n()
    const Icon = categoryIcon(category.slug)
    const Arrow = isRtl ? ArrowLeft : ArrowRight

    return (
        <Link
            href={`/categories/${category.slug}`}
            className={cn('group relative flex min-h-56 flex-col overflow-hidden rounded-3xl bg-gradient-to-br from-navy-50 via-white to-cyan-50/60 p-6 ring-1 ring-navy-100 transition-all duration-300 hover:-translate-y-1 hover:shadow-lift hover:ring-navy-200', className)}
        >
            {category.image && (
                <img src={category.image} alt='' loading='lazy' decoding='async' className='absolute inset-0 h-full w-full object-cover opacity-25 transition-opacity duration-500 group-hover:opacity-35' aria-hidden='true' />
            )}

            <div className='absolute -end-10 -top-10 size-40 rounded-full bg-cyan-200/30 blur-2xl transition-transform duration-700 group-hover:scale-125' aria-hidden='true' />

            <span className='relative flex size-14 items-center justify-center rounded-2xl bg-white text-navy-800 shadow-soft ring-1 ring-navy-100 transition-transform duration-300 group-hover:-rotate-6'>
                <Icon className='size-7' strokeWidth={1.5} aria-hidden='true' />
            </span>

            <div className='relative mt-auto flex flex-col gap-1.5 pt-8'>
                <CmsText table='categories' id={category.id} field={category.name} as='h3' className='text-lg font-semibold leading-snug text-navy-950' />
                {category.description && (
                    <CmsText table='categories' id={category.id} field={category.description} as='p' className='line-clamp-2 text-sm leading-relaxed text-muted-foreground' />
                )}

                <div className='mt-2 flex items-center justify-between'>
                    <span className='text-xs font-medium text-navy-500'>
                        <Num value={category.productCount} />
                        {' '}
                        {tx('منتج', category.productCount === 1 ? 'product' : 'products')}
                    </span>

                    <span className='flex size-9 items-center justify-center rounded-full bg-navy-900 text-white transition-transform duration-300 group-hover:scale-110'>
                        <Arrow className='size-4' aria-hidden='true' />
                    </span>
                </div>
            </div>
        </Link>
    )
}
